/**
 * The app's alert (AlertsController, ManagerScene › Panel): a 1221 × 650 panel
 * over a dark veil, a red top with the title, the message in grey in the
 * middle, and a grey bottom with the buttons as red words — Cancel on the left
 * and OK on the right, split by a thin line. Without Cancel (DisableCancelButton)
 * OK stays alone on the right half, as in the app. Used by the editor and, via
 * ConfirmDialog, by the Home.
 *
 * Escape answers Cancel (or OK when there is no Cancel), Enter answers OK;
 * while busy neither works and the buttons are disabled.
 */
import { useEffect, useId, useRef } from "react";

export function AppAlert({
  title,
  message,
  okLabel,
  cancelLabel,
  onOk,
  onCancel,
  busy = false,
  error,
}: {
  title: string;
  message: string;
  okLabel: string;
  /** with onCancel: the two-button alert */
  cancelLabel?: string;
  onOk: () => void;
  onCancel?: () => void;
  /** waiting for OK's work: "…" on OK, both buttons off */
  busy?: boolean;
  /** shown in red under the message */
  error?: string | null;
}) {
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const answer = useRef({ onOk, onCancel, busy });
  answer.current = { onOk, onCancel, busy };
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    box.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" && e.key !== "Enter") return;
      // the editor beneath must not see it
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat || answer.current.busy) return;
      const { onOk: ok, onCancel: cancel } = answer.current;
      if (e.key === "Enter") ok();
      else (cancel ?? ok)();
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      prev?.focus?.();
    };
  }, []);
  return (
    <div className="ealert-veil">
      <div className="ealert" ref={box} tabIndex={-1} role="alertdialog" aria-modal aria-labelledby={`${id}-t`} aria-describedby={`${id}-m`}>
        <div className="ealert-top">
          <span className="ealert-title" id={`${id}-t`} dir="auto">
            {title}
          </span>
        </div>
        <div className="ealert-message" id={`${id}-m`}>
          <span dir="auto">{message}</span>
          {error && (
            <span className="ealert-error" role="alert" dir="auto">
              {error}
            </span>
          )}
        </div>
        <div className="ealert-bottom">
          {onCancel && (
            <>
              <button className="ealert-btn ealert-cancel" onClick={onCancel} disabled={busy}>
                {cancelLabel}
              </button>
              <span className="ealert-sep" />
            </>
          )}
          <button className="ealert-btn ealert-ok" onClick={onOk} disabled={busy}>
            {busy ? "…" : okLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
